/**
 * What a phase edit would do to the STORED phases after it — asked before the edit is made.
 *
 * ── Why this exists at all ───────────────────────────────────────────────────────────────────
 * A phase operation on a running fork can move a block of the client's calendar that the trainer was
 * not editing. Copying a phase in the middle of a plan pushes every later phase down the cycle axis,
 * and the backend then withdraws and re-dates every not-yet-started occurrence in them. `phaseEdits`
 * REFUSES that only where the consumed prefix is disturbed; everywhere else the act is legal, silent
 * and consequential — the reviewer's case is a copy of the phase the boundary sits exactly at the end
 * of, which moves the client's next Deload from week 5 to week 9 with nothing said.
 *
 * The save-time consent (`structuralEditFlow`) still fires and is not the same answer: it is asked
 * once, at the end, about the whole tree, and by then the trainer has made several edits and cannot
 * tell which one moved what. This one is asked AT THE ACT and names the block that moves.
 *
 * ── It runs the real operation rather than predicting it ─────────────────────────────────────
 * The answer is computed by applying the operation to a COPY and diffing the stored phases'
 * `startCycle`. Predicting the arithmetic instead would be a second implementation of
 * `rebasePhaseWindows` that agrees with it until the first time it does not — and this answer is
 * shown to the trainer as a promise about what the next press will do, so a drift here is a lie
 * rather than a rendering slip. `produce` is a pure function of its inputs and mutates nothing the
 * caller owns; the draft it builds is thrown away.
 *
 * ── A LIBRARY template answers nothing, deliberately ─────────────────────────────────────────
 * It has no client schedule to disturb, so there is nothing to warn about and a dialog there would be
 * a confirmation for an edit that is free and undoable. The gate is `isForkTemplate`, the same one
 * `pendingStructuralEdits` opens on.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. It answers in NUMBERS and the
 * phase's own name — the sentence is the page's.
 */

import { produce } from 'immer';

import { duplicatePhase, removePhase, setPhaseCycles } from './draftOperations';
import type { PlanDraft } from './planDraft.types';

/** One phase operation, addressed the way the band addresses it. */
export type PhaseEdit =
  | { readonly kind: 'duplicate'; readonly phaseIndex: number }
  | { readonly kind: 'remove'; readonly phaseIndex: number }
  | { readonly kind: 'resize'; readonly phaseIndex: number; readonly cycles: number };

/** A stored phase whose window the edit would move, and where it would move it to. */
export interface PhaseWindowShift {
  readonly name: string;
  readonly fromStartCycle: number;
  readonly toStartCycle: number;
}

/**
 * The name the preview's copy is minted with.
 *
 * It plays no part in WHICH windows move — the copy carries the source's length wherever it is named
 * — but `duplicatePhase` refuses a blank one, so the dry run needs something. A constant rather than
 * the caller's real name keeps that out of this module's interface.
 */
const PREVIEW_COPY_NAME = 'preview';

/**
 * Which STORED phases this edit would move, and to where. Empty when it moves none — a library
 * template, an append at the tail, a resize of the last phase, or an edit the gates refuse outright.
 *
 * ONE entry point over the three operations rather than three predicates, because the question is one
 * question and every answer is the same diff. A caller asking about a kind this does not model would
 * be asking about an operation the band cannot dispatch.
 */
export function phaseWindowShifts(draft: PlanDraft, edit: PhaseEdit): PhaseWindowShift[] {
  if (!draft.live.isForkTemplate) return [];

  const after = produce(draft, (mutable) => {
    if (edit.kind === 'duplicate') duplicatePhase(mutable, edit.phaseIndex, PREVIEW_COPY_NAME);
    else if (edit.kind === 'remove') removePhase(mutable, edit.phaseIndex);
    else setPhaseCycles(mutable, edit.phaseIndex, edit.cycles);
  });
  // A refused operation records nothing, and Immer hands back the same tree it was given.
  if (after === draft) return [];

  const before = new Map(
    draft.phases
      .filter((phase) => phase.serverId != null)
      .map((phase) => [phase.serverId, phase.startCycle] as const),
  );

  const shifts: PhaseWindowShift[] = [];
  for (const phase of after.phases) {
    if (phase.serverId == null) continue;
    const from = before.get(phase.serverId);
    // A phase the edit DELETED is gone rather than moved, and a phase it created was never stored.
    if (from === undefined || from === phase.startCycle) continue;
    shifts.push({ name: phase.name, fromStartCycle: from, toStartCycle: phase.startCycle });
  }
  return shifts;
}
